/** Local GSAP reveals: one entrance, no scroll hijacking, no hidden-content dependency. */
(function () {
    'use strict';

    var boot = window.ddMotionBoot;
    if (!boot || boot.state !== 'pending') return;
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) { boot.release(); return; }

    var records = new Map();
    var triggers = [];
    var refreshTimer;
    var stopped = false;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var compact = window.matchMedia('(max-width: 767px)').matches;

    // Animate carousel wrappers, never Owl's slides/clones or transforms.
    // Accordion/tab contents are left alone so opening them remains immediate.
    var blockSelectors = [
        '.section-header > *', '.text-image-content > *', '.text-image-media-column',
        '.text-accordion-content > *', '.text-accordion-item',
        '.grid-cards-mode-grid > .grid-card-item', '.grid-card-slider',
        '.home-options-service-image', '.testimonials-intro > *', '.testimonial-card',
        '.process-steps-heading > *', '.process-steps-intro', '.process-step',
        '.master-text-content > *', '.column-content',
        '.block-cpt-taxonomy-heading', '.block-cpt-grid > .block-cpt-item',
        '.block-cpt-slider', '.block-cpt-pagination', '.taxonomy-posts-group-header',
        '.taxonomy-posts-grid > .block-cpt-item', '.taxonomy-posts-slider',
        '.gallery-block:not(.gallery-slider) > .gallery-item', '.gallery-slider',
        '.company-team-media-placeholder', '.logos-list:not(.logos-slider) > .logos-item',
        '.logos-slider', '.tables-group', '.bg-with-text-content > *',
        '.accordion-tabs-nav', '.accordion-tabs-content', '.accordion-tabs-accordion > .card',
        '.block_buttons .dd-buttons', '.contact-detail', '.contact-promo'
    ].join(',');

    function restore(record) {
        if (record.tween) record.tween.kill();
        if (record.imageCleanup) record.imageCleanup();
        record.el.style.removeProperty('--dd-motion-y');
        if (record.opacity) record.el.style.setProperty('opacity', record.opacity, record.priority);
        else record.el.style.removeProperty('opacity');
        record.el.dataset.ddMotion = 'done';
        record.done = true;
    }

    function finishAll() {
        stopped = true;
        triggers.forEach(function (trigger) { trigger.kill(); });
        records.forEach(restore);
        window.clearTimeout(refreshTimer);
        boot.release();
        boot.state = 'disabled';
    }

    function refresh() {
        if (stopped) return;
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(function () { ScrollTrigger.refresh(); }, 120);
    }

    function play(record, delay) {
        if (!record || record.done || record.started || stopped) return;
        record.started = true;
        function start() {
            if (record.done || stopped) return;
            record.el.dataset.ddMotion = 'running';
            record.tween = gsap.to(record.el, {
                opacity: record.endOpacity,
                '--dd-motion-y': '0px',
                duration: compact ? 0.55 : (record.media ? 0.95 : 0.75),
                delay: delay || 0,
                ease: 'power2.out',
                overwrite: 'auto',
                onComplete: function () { restore(record); }
            });
        }
        // A hero photo that arrives slightly late should also fade in, not pop in.
        var img = record.el.matches('.page-hero-home-media-frame') ? record.el.querySelector('img') : null;
        if (img && !img.complete) {
            var timer;
            var ready = function () { record.imageCleanup(); start(); };
            record.imageCleanup = function () {
                window.clearTimeout(timer);
                img.removeEventListener('load', ready);
                img.removeEventListener('error', ready);
            };
            img.addEventListener('load', ready, { once: true });
            img.addEventListener('error', ready, { once: true });
            timer = window.setTimeout(ready, 1200);
        } else start();
    }

    function targetsFor(root) {
        var selector = blockSelectors;
        if (root.dataset.motionRoot === 'hero') {
            selector = root.classList.contains('page-hero-slider') ? '.carousel' :
                '.page-hero-subtitle, .page-hero-title, .page-hero-description > *, .page-hero-buttons, .page-hero-home-media-frame, .home-hero-bubbles';
        } else if (root.dataset.motionRoot === 'cta') {
            selector = '.footer-cta__content > *, .footer-cta__person';
        }
        var candidates = Array.from(root.querySelectorAll(selector));
        // Safe coverage for future blocks as well as currently unused block variants.
        if (!candidates.length) candidates = Array.from(root.children).filter(function (el) {
            return !el.classList.contains('section-background-layer');
        });
        return candidates.filter(function (el) {
            return el.getClientRects().length && !el.closest('.owl-item, .collapse:not(.show), .tab-pane:not(.active), [data-motion="off"]') &&
                !candidates.some(function (ancestor) { return ancestor !== el && ancestor.contains(el); });
        });
    }

    function init() {
        if (boot.state !== 'pending') return;
        if (reduce.matches) { boot.release(); return; }
        try {
            gsap.registerPlugin(ScrollTrigger);
            var navigation = performance.getEntriesByType('navigation')[0];
            var restored = window.scrollY > 0 || !!location.hash || (navigation && navigation.type === 'back_forward');
            var scrollTargets = [];
            var heroTargets = [];

            document.querySelectorAll('[data-motion-root]').forEach(function (root) {
                targetsFor(root).forEach(function (el) {
                    var rect = el.getBoundingClientRect();
                    if (rect.bottom <= 0 || (restored && rect.top < innerHeight)) return;
                    var style = getComputedStyle(el);
                    var media = el.matches('.page-hero-home-media-frame, .text-image-media-column, .footer-cta__person, .home-hero-bubbles');
                    var record = {
                        el: el, media: media, done: false, started: false,
                        opacity: el.style.getPropertyValue('opacity'),
                        priority: el.style.getPropertyPriority('opacity'),
                        endOpacity: Number(style.opacity)
                    };
                    // A fallback wrapper inherits the initial guard's opacity; its final state is visible.
                    if (el.parentElement === root) record.endOpacity = 1;
                    records.set(el, record);
                    el.dataset.ddMotion = 'waiting';
                    gsap.set(el, { opacity: 0, '--dd-motion-y': media ? '0px' : (compact ? '12px' : '22px') });
                    if (root.dataset.motionRoot === 'hero' && rect.top < innerHeight) heroTargets.push(record);
                    else scrollTargets.push(el);
                });
            });

            triggers = ScrollTrigger.batch(scrollTargets, {
                start: 'top 92%', once: true, interval: 0.06, batchMax: compact ? 2 : 4,
                onEnter: function (elements) {
                    elements.forEach(function (el, index) { play(records.get(el), index * (compact ? 0.045 : 0.075)); });
                },
                onEnterBack: function (elements) {
                    elements.forEach(function (el) { play(records.get(el), 0); });
                }
            });

            // All target start states exist before lifting the first-paint guard.
            boot.state = 'active';
            boot.release();
            var textIndex = 0;
            heroTargets.forEach(function (record) {
                play(record, record.media ? 0.12 : Math.min(textIndex++ * 0.085, 0.34));
            });
            ScrollTrigger.refresh();

            // Keyboard navigation and anchor links must never land on transparent content.
            document.addEventListener('focusin', function (event) {
                records.forEach(function (record) {
                    if (!record.done && record.el.contains(event.target)) restore(record);
                });
            });
            window.addEventListener('hashchange', finishAll);
            window.addEventListener('beforeprint', finishAll);
            window.addEventListener('pageshow', function (event) { if (event.persisted) finishAll(); });
            reduce.addEventListener('change', function (event) { if (event.matches) finishAll(); });
            // Lazy images, carousels and Bootstrap disclosures can move later sections.
            document.addEventListener('load', function (event) {
                if (event.target.tagName === 'IMG') refresh();
            }, true);
            if (document.fonts) document.fonts.ready.then(refresh);
            window.addEventListener('load', refresh, { once: true });
            if (window.jQuery) window.jQuery(document).on(
                'shown.bs.collapse hidden.bs.collapse shown.bs.tab refreshed.owl.carousel', refresh
            );
        } catch (error) {
            finishAll();
            console.warn('Page motion disabled; content remains visible.', error);
        }
    }

    // The footer bundle runs after markup. jQuery queues this after carousel setup.
    if (window.jQuery) window.jQuery(init);
    else if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
    else init();
})();
